<template>
  <div class="ai-hero">
    <header class="ai-hero__intro">
      <div class="ai-hero__logo" aria-hidden="true">
        <v-icon color="#fff" size="28">mdi-creation</v-icon>
      </div>
      <div class="ai-hero__intro-copy">
        <h2 class="ai-hero__title">
          {{ agent.name }}
          <span class="ai-hero__title-tag">{{ agent.tagline }}</span>
        </h2>
        <p class="ai-hero__subtitle">{{ agent.description }}</p>
      </div>
    </header>

    <section class="ai-hero__caps" aria-label="What this agent can do">
      <h3 class="ai-hero__section-heading">What {{ agent.name }} can do</h3>
      <ul class="ai-hero__cap-list">
        <li v-for="cap in agent.capabilities" :key="cap" class="ai-hero__cap">
          <v-icon size="15" :color="agent.accent">mdi-check-circle</v-icon>
          <span>{{ cap }}</span>
        </li>
      </ul>
    </section>

    <section v-if="agent.suggestedPrompts && agent.suggestedPrompts.length" class="ai-hero__starters">
      <h3 class="ai-hero__section-heading">Try asking</h3>
      <ul class="ai-hero__starter-list">
        <li v-for="(suggestion, i) in agent.suggestedPrompts" :key="i">
          <button type="button" class="ai-hero__starter" @click="$emit('suggestion', suggestion)">
            <span class="ai-hero__starter-text">{{ suggestion }}</span>
            <v-icon small class="ai-hero__starter-icon">mdi-arrow-right</v-icon>
          </button>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import { agentByKey } from '../agentVersions.js';

export default {
  name: 'TraceyAiEmptyHero',
  props: {
    /** Which agent is currently selected ("ultra" | "sprint"). */
    agentKey: {
      type: String,
      default: 'ultra',
    },
  },
  computed: {
    agent() {
      return agentByKey(this.agentKey);
    },
  },
};
</script>

<style scoped>
.ai-hero {
  display: flex;
  flex-direction: column;
  gap: 22px;
  width: 100%;
  max-width: 56rem;
  margin: auto; /* centres vertically when there's room; see .ai-main__feed--empty */
  padding: 8px 20px 16px;
  text-align: left;
}

.ai-hero__intro {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.ai-hero__logo {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: linear-gradient(135deg, #12253a 0%, #1e3a5f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(18, 37, 58, 0.18);
}

.ai-hero__intro-copy {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.ai-hero__title {
  margin: 0 0 6px;
  font-size: 1.5rem;
  font-weight: 600;
  color: #0d0d0d;
  letter-spacing: -0.02em;
  line-height: 1.25;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
}

.ai-hero__title-tag {
  font-size: 13px;
  font-weight: 600;
  color: #8e8ea0;
  letter-spacing: 0;
}

.ai-hero__subtitle {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #6e6e80;
}

.ai-hero__section-heading {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8e8ea0;
}

.ai-hero__caps {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e6ecf3;
  background: #f7f9fc;
}

.ai-hero__cap-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
}

.ai-hero__cap {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #353740;
}

.ai-hero__starter-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-hero__starter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e3e3e8;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.ai-hero__starter:hover {
  border-color: #c5cdd8;
  background: #f7f7f8;
  box-shadow: 0 2px 8px rgba(18, 37, 58, 0.06);
}

.ai-hero__starter-text {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #353740;
}

.ai-hero__starter-icon {
  flex-shrink: 0;
  color: #8e8ea0 !important;
}

.ai-hero__starter:hover .ai-hero__starter-icon {
  color: #ff1463 !important;
}

@media (max-width: 640px) {
  .ai-hero__cap-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .ai-hero {
    padding: 4px 12px 12px;
    gap: 16px;
  }

  .ai-hero__intro {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .ai-hero__title {
    font-size: 1.35rem;
    justify-content: center;
  }
}
</style>
